import React, { useState, useEffect } from 'react';
import { Button, Modal, message } from 'antd';
import config from '../config';

const VERSION_MANIFEST_WARNING_KEY = 'version-manifest-warning';
const CHECK_FOR_UPDATE_INTERVAL_MS = 1000 * 60 * 10;

const getVersionManifestUrl = () => {
  const url = new URL(config.versionManifestUrl, window.location.origin);
  url.searchParams.set('t', Date.now().toString());
  return url.toString();
};

const fetchVersionManifest = async () => {
  const response = await fetch(getVersionManifestUrl(), {
    cache: 'no-store',
    headers: {
      'Cache-Control': 'no-store',
      Pragma: 'no-cache'
    }
  });

  if (!response.ok) {
    throw new Error(`Version manifest request failed with ${response.status}`);
  }

  const manifest = await response.json();

  if (typeof manifest?.buildId !== 'string' || !manifest.buildId.trim()) {
    throw new Error('Version manifest is missing buildId');
  }

  return manifest;
};


export const shouldCheckForVersionUpdates = (buildId) => !!buildId;

export const useVersionUpdater = () => {
  const [updateFound, setUpdateFound] = useState(false);
  const [triggerUpdateCheckAt, setTriggerUpdateCheckAt] = useState(null);

  const updateApp = async () => {
    return fetchVersionManifest()
      .then((manifest) => {
        if (config.buildId === manifest.buildId.trim()) return;
        setUpdateFound(true);

        Modal.info({
          title: 'Veritas App Update Found!',
          content: `Click Update to refresh the page and get the latest version${manifest.releaseVersion ? ` (${manifest.releaseVersion})` : ''}`,
          mask: false,
          okText: 'Update',
          cancelButtonProps: { style: { hidden: true } },
          maskClosable: true,
          onOk: () => window.location.reload(),
          onCancel: () => message.info({ 
            duration: null, 
            content: (
              <span>
                <span style={{ marginRight: 10 }}>Outdated versions can cause unexpected behaviors. Click Update to refresh the page and get the latest version</span>
                <Button type='default' onClick={() => window.location.reload()}>Update</Button> 
              </span>
            )
          })
        })
      })
      .catch((error) => {
        message.warning({
          key: VERSION_MANIFEST_WARNING_KEY,
          duration: 6,
          content: 'Unable to check for app updates. The app will try again later.'
        });
        console.warn('Unable to fetch version manifest', error);
      });
  }


  useEffect(() => {
    if (!updateFound && shouldCheckForVersionUpdates(config.buildId)) {
      let checkUpdateInTenMins;
      updateApp()
        .finally(() => {
          checkUpdateInTenMins = setTimeout(() => setTriggerUpdateCheckAt(Date.now()), CHECK_FOR_UPDATE_INTERVAL_MS) // 10 mins
        });

      return () => clearTimeout(checkUpdateInTenMins);
    }

  }, [triggerUpdateCheckAt]);
}